import { rem } from 'polished';
import React, { SyntheticEvent } from 'react';
import styled from 'styled-components';

import { FatalAttraction as FA } from '@vpr/tracking';

import { bokehTheme as theme } from '@vimeo/bokeh-theme';
import { Button } from '@vimeo/iris/components';
import { DownloadArrow } from '@vimeo/iris/icons';
import { VimeostockLight } from '@vimeo/iris/illustration';
import { themes } from '@vimeo/iris/themes';
import { Link, Paragraph } from '@vimeo/iris/typography';
import { TeamUserPermissionLevel, User } from '@vimeo/viewer';

import ClipStockDetails from '../../components/ClipCard/ClipStockDetails';
import { ClipDataObject, PictureSize } from '../../types';
import { STOCK_PRIVACY as PRIVACY_STOCK_FOOTAGE, VIDEO_KEY } from '../../utils/constants';
import FATAL_ATTRACTION_CONFIG from '../../utils/fatalAttractionConfig';
import { getTimecodeFromSeconds, idFromUri } from '../../utils/helpers';
import stockService from '../../utils/stock';
import EssentialsBadge from '../EssentialsBadge';

const Wrapper = styled.div`
  display: flex;
  display: grid;
  min-width: ${rem(330)};
  max-width: ${rem(1600)};
  margin: 0 auto;
  flex-flow: row wrap;
  justify-content: space-between;
  padding: ${rem(20)} ${rem(40)};
  grid-template-columns: repeat(auto-fill, minmax(${rem('256px')}, 1fr));

  &:after {
    content: '';
    flex: auto;
  }
`;

const PlayerLayoutGridItem = styled.div`
  padding: ${rem(12)};
  height: ${rem(190)};
  max-height: 14rem;
  flex: 0 1 ${rem(256)};
  position: relative;

  @media only screen and (max-width: 1600px) {
    height: 13vw;
  }

  @media only screen and (max-width: 1359px) {
    height: 16vw;
  }

  @media only screen and (max-width: 1103px) {
    height: 19vw;
  }

  @media only screen and (max-width: 847px) {
    height: 28vw;
  }

  @media only screen and (max-width: 591px) {
    width: 100%;
    height: 54vw;
    padding: 0;
  }
`;

const GridBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  overflow: hidden;
`;

const GridItemWrapper = styled.div<{
  brandColor?: string;
  isSelected?: boolean;
  isStock?: boolean;
}>`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  padding: ${rem(8)};
  box-shadow: ${(props) => (props.isSelected ? `0 0 0 2px ${props.brandColor}` : null)};
  border-radius: ${(props) => (props.isSelected ? '3px' : null)};
  overflow: ${(props) => (props.isStock ? 'auto' : null)};
`;

const GridItemThumbContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  text-align: center;
  background: black;
`;

const GridItemThumb = styled.img`
  height: 100%;
  width: 100%;
  object-fit: contain;
  color: black;
`;

const StockIcon = styled(VimeostockLight)`
  height: ${rem(18)};
  position: absolute;
  right: ${rem(10)};
  top: ${rem(10)};
  width: ${rem(80)};
`;

const Badge = styled.span`
  height: ${rem(18)};
  position: absolute;
  left: ${rem(10)};
  top: ${rem(11)};
  width: ${rem(80)};
`;

const MetaInfo = styled.div<{
  isStock?: boolean;
}>`
  display: flex;
  align-items: flex-end;
  position: absolute;
  bottom: ${rem(8)};
  left: ${rem(8)};
  right: ${rem(8)};
  height: 60px;
  padding: ${rem(16)} ${rem(8)} ${rem(8)};
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, black 100%);
  align-items: ${(props) => (props.isStock ? 'initial' : null)};

  p {
    color: ${(props) => !props.isStock && 'white'};
    margin-bottom: 0;
  }
`;

const MetaGrid = styled.div<{
  isStock?: boolean;
}>`
  position: relative;
  padding: 0;
  width: 100%;

  @media only screen and (min-width: 1024px) and (max-width: 1280px) {
    padding-left: ${(props) => (props.isStock ? 0 : null)};
  }
`;

const MetaInfoName = styled.div<{
  isStock?: boolean;
}>`
  p,
  strong {
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: inline-block;
    vertical-align: bottom;
  }
  align-self: ${(props) => (props.isStock ? 'flex-start' : null)};
  padding-top: ${(props) => (props.isStock ? '1rem' : null)};
  max-width: ${(props) => (props.isStock ? '70%' : '85%')};

  @media only screen and (max-width: 767px) {
    width: ${(props) => (props.isStock ? '75%' : null)};
    height: ${(props) => (props.isStock ? rem(40) : null)};
    p {
      line-height: ${(props) => (props.isStock ? rem(15) : null)};
    }
  }
`;

const MetaInfoDuration = styled.div<{
  isStock?: boolean;
}>`
  display: flex;
  text-align: right;
  align-self: ${(props) => (props.isStock ? 'flex-start' : 'flex-end')};
  @media only screen and (max-width: 767px) {
    width: ${(props) => (props.isStock ? '20%' : null)};
  }
`;

const ClipTitle = styled.strong<{
  hoverColor?: string;
}>`
  color: white;
  &:hover {
    color: ${(props) => props.hoverColor || theme.colors.blue['500']};
  }
`;

const StyledDownloadIcon = styled(DownloadArrow)`
  path {
    fill: white;
  }
`;

interface Props {
  albumId: number;
  brandColor: string;
  displayedVideos: ClipDataObject[];
  onSelect: (index: number) => void;
  selectedVideoIndex: number;
  curUser?: User;
  isOffsite: boolean;
  allowDownloads: boolean;
  setDownloadClipId?: (id: number) => void;
  openDownloadModal?: () => void;
}

/**
 * Player Layout Grid
 */
const PlayerLayoutGrid = ({
  brandColor,
  displayedVideos,
  onSelect,
  selectedVideoIndex,
  curUser,
  isOffsite,
  allowDownloads,
  setDownloadClipId,
  openDownloadModal,
  ...props
}: Props) => {
  const isImpersonatingViewerOrContrib =
    curUser?.teamUserPermissionLevel &&
    (curUser.teamUserPermissionLevel === TeamUserPermissionLevel.Contributor ||
      curUser.teamUserPermissionLevel === TeamUserPermissionLevel.Viewer);

  return (
    <Wrapper>
      {displayedVideos.map((video, index) => {
        const thumbnailSrcSet = video.pictures.sizes
          .map((thumb: PictureSize) => {
            return `${thumb.link} ${thumb.width}w`;
          })
          .join(', ');

        const videoId =
          video.displayType === 'CLIP_PLACEHOLDER' ? index : idFromUri(video?.uri, VIDEO_KEY);

        // Get the 640-width thumbnail (if available) for src fallback:
        const thumbnail =
          video.pictures.sizes.length > 0 &&
          video.pictures.sizes.find((picture) => picture.width === 640);
        const thumbnailSrc = thumbnail ? thumbnail.link : undefined;

        // Link to contextul clip page for the album
        const contextualLink = `/showcase/${props.albumId}/video/${videoId}`;
        const stockClipPageLink = `/${videoId}`;
        const isStock =
          video.displayType === 'CLIP_PLACEHOLDER'
            ? false
            : video.privacy.view === PRIVACY_STOCK_FOOTAGE;
        const isFree = isStock && video.isFree;

        return (
          <PlayerLayoutGridItem key={videoId}>
            <GridItemWrapper
              brandColor={brandColor}
              isSelected={selectedVideoIndex === index}
              isStock={isStock}
              onClick={() => {
                onSelect(index);
                FA.trackClick({
                  container: 'showcase',
                  component: 'featured_video_row',
                  keyword: index,
                });
              }}
            >
              <GridItemThumbContainer>
                <GridItemThumb srcSet={thumbnailSrcSet} src={thumbnailSrc} alt={video.name} />
                {isFree && (
                  <Badge>
                    <EssentialsBadge />
                  </Badge>
                )}
                {isStock && (
                  <a href="/stock">
                    <StockIcon />
                  </a>
                )}
              </GridItemThumbContainer>
              <MetaInfo isStock={isStock}>
                <MetaGrid isStock={isStock}>
                  <GridBlock>
                    <MetaInfoName isStock={isStock}>
                      <Paragraph size="2">
                        {isOffsite ||
                        isImpersonatingViewerOrContrib ||
                        video.displayType === 'CLIP_PLACEHOLDER' ? (
                          <ClipTitle title={video.name} hoverColor={brandColor}>
                            {video.name}
                          </ClipTitle>
                        ) : (
                          <Link
                            href={isStock ? stockClipPageLink : contextualLink}
                            format="basic"
                            theme={themes.dark}
                            onClick={() => {
                              if (isStock) {
                                FA.trackClick({
                                  ...FATAL_ATTRACTION_CONFIG.faSpecs.albumTitle,
                                  keyword: videoId,
                                });
                              }
                            }}
                          >
                            <ClipTitle title={video.name} hoverColor={brandColor}>
                              {video.name}
                            </ClipTitle>
                          </Link>
                        )}
                      </Paragraph>
                    </MetaInfoName>
                    <MetaInfoDuration isStock={isStock}>
                      {isStock && curUser ? (
                        <ClipStockDetails
                          clipId={videoId}
                          pictures={video.pictures}
                          // @ts-ignore
                          // Temp fix for error TS2322: Type 'void' is not assignable to type 'string'.
                          // This is accessing globalThis.name, rather than anything
                          // in the local scope.
                          name={name}
                          user={curUser}
                          featured
                          stockService={stockService}
                          isFree={isFree}
                        />
                      ) : allowDownloads && video.privacy.download ? (
                        <Button
                          icon={<StyledDownloadIcon />}
                          format="basic"
                          variant="hyperminimal"
                          size="md"
                          onClick={(e: SyntheticEvent) => {
                            e.stopPropagation();
                            // handle download
                            if (
                              video.displayType !== 'CLIP_PLACEHOLDER' &&
                              setDownloadClipId &&
                              openDownloadModal
                            ) {
                              setDownloadClipId(videoId);
                              openDownloadModal();
                            }
                          }}
                        />
                      ) : (
                        <Paragraph size="3">
                          {getTimecodeFromSeconds(video.duration, 'mm:ss')}
                        </Paragraph>
                      )}
                    </MetaInfoDuration>
                  </GridBlock>
                </MetaGrid>
              </MetaInfo>
            </GridItemWrapper>
          </PlayerLayoutGridItem>
        );
      })}
    </Wrapper>
  );
};

export default PlayerLayoutGrid;
